import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2, GraduationCap } from 'lucide-react';

const STEPS = ['University Information', 'Accreditation Details', 'Programmes Offered', 'Subscription'];

export default function UniversityOnboardingWizard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [info, setInfo] = useState({
    name: '', university_type: 'university', website: '', email: '', phone: '',
    country: '', location: '', about: '',
  });
  const [accreditation, setAccreditation] = useState({
    accreditation: '', degree_levels: [], founded_year: '', total_students: '',
  });
  const [programs, setPrograms] = useState('');

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const toggleDegreeLevel = (level) => {
    setAccreditation(a => ({
      ...a,
      degree_levels: a.degree_levels.includes(level) ? a.degree_levels.filter(l => l !== level) : [...a.degree_levels, level],
    }));
  };

  const finishStep1 = () => {
    if (!info.name.trim()) {
      toast.error('Institution name is required');
      return;
    }
    next();
  };

  const finishStep3 = async () => {
    setSaving(true);
    try {
      await api.put('/universities/my-profile', {
        ...info,
        accreditation: accreditation.accreditation,
        degree_levels: accreditation.degree_levels,
        founded_year: accreditation.founded_year ? parseInt(accreditation.founded_year, 10) : null,
        total_students: accreditation.total_students ? parseInt(accreditation.total_students, 10) : null,
        specializations: programs.split(',').map(s => s.trim()).filter(Boolean),
      });
      next();
    } catch {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        <div className="flex items-center justify-center gap-2 mb-6">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${i <= step ? 'bg-[#003366] text-white' : 'bg-gray-200 text-gray-500'}`}>
                {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && <div className={`w-8 h-0.5 ${i < step ? 'bg-[#003366]' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <GraduationCap className="w-5 h-5 text-[#003366]" />
              <CardTitle>{STEPS[step]}</CardTitle>
            </div>
            <CardDescription>Step {step + 1} of {STEPS.length} — set up your institution profile</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {step === 0 && (
              <>
                <div className="space-y-2">
                  <Label>Institution Name *</Label>
                  <Input value={info.name} onChange={(e) => setInfo({ ...info, name: e.target.value })} placeholder="University of ..." />
                </div>
                <div className="space-y-2">
                  <Label>Type</Label>
                  <select
                    value={info.university_type}
                    onChange={(e) => setInfo({ ...info, university_type: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#003366]/20"
                  >
                    <option value="university">University</option>
                    <option value="college">College</option>
                    <option value="school">School</option>
                    <option value="training_provider">Training Provider</option>
                    <option value="bootcamp">Bootcamp</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Website</Label>
                    <Input value={info.website} onChange={(e) => setInfo({ ...info, website: e.target.value })} placeholder="https://..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input value={info.phone} onChange={(e) => setInfo({ ...info, phone: e.target.value })} placeholder="+44 ..." />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input value={info.country} onChange={(e) => setInfo({ ...info, country: e.target.value })} placeholder="United Kingdom" />
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input value={info.location} onChange={(e) => setInfo({ ...info, location: e.target.value })} placeholder="City" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>About</Label>
                  <Textarea rows={4} value={info.about} onChange={(e) => setInfo({ ...info, about: e.target.value })} placeholder="Tell prospective students about your institution..." />
                </div>
                <div className="flex justify-end pt-2">
                  <Button onClick={finishStep1} className="bg-[#003366] hover:bg-[#003366]/90">
                    Next<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <div className="space-y-2">
                  <Label>Accreditation Body</Label>
                  <Input value={accreditation.accreditation} onChange={(e) => setAccreditation({ ...accreditation, accreditation: e.target.value })} placeholder="e.g. QAA, Ofsted" />
                </div>
                <div className="space-y-2">
                  <Label>Degree Levels Offered</Label>
                  <div className="flex flex-wrap gap-2">
                    {['foundation', 'undergraduate', 'postgraduate', 'phd'].map(level => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => toggleDegreeLevel(level)}
                        className={`px-3 py-1.5 rounded-full text-sm border capitalize ${accreditation.degree_levels.includes(level) ? 'bg-[#003366] text-white border-[#003366]' : 'border-gray-300 text-gray-700'}`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Founded Year</Label>
                    <Input type="number" value={accreditation.founded_year} onChange={(e) => setAccreditation({ ...accreditation, founded_year: e.target.value })} placeholder="e.g. 1990" />
                  </div>
                  <div className="space-y-2">
                    <Label>Total Students</Label>
                    <Input type="number" value={accreditation.total_students} onChange={(e) => setAccreditation({ ...accreditation, total_students: e.target.value })} placeholder="e.g. 5000" />
                  </div>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={next} className="bg-[#003366] hover:bg-[#003366]/90">
                    Next<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label>Programmes/Subject Areas (comma-separated)</Label>
                  <Textarea rows={4} value={programs} onChange={(e) => setPrograms(e.target.value)} placeholder="Computer Science, Business, Engineering, Law" />
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={finishStep3} disabled={saving} className="bg-[#003366] hover:bg-[#003366]/90">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}Next<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <div className="text-center py-4">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="font-semibold text-lg mb-1">Your institution profile is ready!</h3>
                  <p className="text-muted-foreground text-sm">
                    Choose a University plan to publish programmes and reach prospective students.
                  </p>
                </div>
                <div className="flex justify-between pt-2">
                  <Button variant="outline" onClick={back}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
                  <Button onClick={() => navigate('/pricing')} className="bg-[#003366] hover:bg-[#003366]/90">
                    Choose Subscription<ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
                <div className="text-center">
                  <button onClick={() => navigate('/university/dashboard')} className="text-sm text-muted-foreground hover:text-foreground underline mt-2">
                    Skip, go to dashboard
                  </button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
